import { useState, useRef, useEffect } from "react";
import { UI, T } from "../constants/tokens";
import { getThemePref, setThemePref, useThemePref } from "../utils/theme";
import Icon from "./Icon";

// ─── Day / night / automatic, from the header ────────────────────────────────
// The switch used to live only at the bottom of Settings: burger → scroll a
// twelve-item menu → Settings → scroll again. Four actions for a choice people
// make by reflex when the light in the room changes.
//
// It is a labelled control, not a bare icon (David, 2026-09-23). An icon alone
// never says what it is about — a moon in a header could be notifications,
// night mode, or a bookmark — so the button carries the CURRENT mode in a word
// ("יום" · "לילה" · "אוטומטי") beside the icon, and opening it shows all three
// named in full. Three options do not fit a two-state toggle, and "follow the
// phone" is the one most people actually want.
//
// Not gold: neither the primary action nor a brand mark. It wears the same
// quiet chrome as the sign-out and burger buttons beside it (DESIGN §3).

export const THEME_OPTIONS = [
  { id: "light", icon: "sun",   short: "יום",   label: "אור יום" },
  { id: "dark",  icon: "moon",  short: "לילה",  label: "לילה" },
  { id: "auto",  icon: "phone", short: "אוטומטי",  label: "אוטומטי · לפי הטלפון" },
];

export default function ThemeToggle({ compact = false }) {
  const { pref, theme } = useThemePref();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Close on an outside tap or Escape — a menu that can only be closed by
  // choosing something is a trap on a phone.
  useEffect(() => {
    if (!open) return;
    const onDown = e => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = THEME_OPTIONS.find(o => o.id === pref) || THEME_OPTIONS[2];
  // Under "auto" the icon shows what the phone is currently giving, so the
  // button never contradicts the screen behind it.
  const shownIcon = pref === "auto" ? (theme === "dark" ? "moon" : "sun") : current.icon;

  return (
    <div ref={boxRef} style={{ position: "relative", flexShrink: 0 }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu" aria-expanded={open}
        aria-label={`מצב תצוגה · ${current.label}`}
        style={{
          background: "transparent",
          border: `1px solid ${UI.line.hairline}`,
          borderRadius: 8,
          minHeight: compact ? 36 : 44,
          padding: compact ? "0 8px" : "0 10px",
          cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6,
          fontFamily: "inherit", color: UI.text.secondary,
          fontSize: 12.5, fontWeight: 700, whiteSpace: "nowrap",
        }}>
        <Icon name={shownIcon} size={compact ? 16 : 18} color={UI.text.secondary}/>
        <span>{current.short}</span>
      </button>

      {open && (
        // No fade and a surface one step ABOVE the cards. The menu opened at
        // opacity 0 and, in the night theme, sat on the same shade as the
        // card under it, so it read as a see-through layer over the content
        // (phone check, 25.09). It is opaque from its first frame now.
        <div role="menu" style={{
          // Opens INWARD: the button sits at the edge of the header, so the
          // menu is pinned by its far edge and grows toward the middle of the
          // screen. Anchored the other way it ran off the viewport and gave
          // the whole page a horizontal scroll.
          position: "absolute", top: "calc(100% + 6px)", insetInlineEnd: 0,
          minWidth: 208, maxWidth: "min(240px, calc(100vw - 24px))", zIndex: 300,
          background: UI.surface.overlay,
          border: `1px solid ${UI.line.strong}`,
          borderRadius: T.radius.md,
          boxShadow: UI.shadow.pop,
          padding: 4, overflow: "hidden",
        }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: UI.text.muted, padding: "6px 10px 4px" }}>מצב תצוגה</div>
          {THEME_OPTIONS.map(o => {
            const active = pref === o.id;
            return (
              <button key={o.id} role="menuitemradio" aria-checked={active}
                onClick={() => { setThemePref(o.id); setOpen(false); }}
                style={{
                  width: "100%", minHeight: 44, padding: "0 10px",
                  background: active ? UI.state.surface : "transparent",
                  color: active ? UI.state.fg : UI.text.secondary,
                  border: "none", borderRadius: 8, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 9,
                  fontFamily: "inherit", fontSize: 14, fontWeight: active ? 800 : 600,
                  textAlign: "start",
                }}>
                <Icon name={o.icon} size={16} color={active ? UI.state.fg : UI.text.secondary}/>
                <span style={{ flex: 1, textAlign: "start" }}>{o.label}</span>
                {active && <Icon name="check" size={15} color={UI.state.fg}/>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// The same three options as a row of buttons, for the settings screen.
export function ThemeChoice() {
  const { pref } = useThemePref();
  return (
    <div style={{ display: "flex", gap: 8 }}>
      {THEME_OPTIONS.map(o => {
        const active = pref === o.id;
        return (
          <button key={o.id} onClick={() => { if (!active) setThemePref(o.id); }}
            aria-pressed={active}
            style={{
              flex: 1, minHeight: 44, padding: "10px 8px", borderRadius: 6,
              border: `1px solid ${active ? UI.state.surface : UI.line.hairline}`,
              background: active ? UI.state.surface : "transparent",
              color: active ? UI.state.fg : UI.text.secondary,
              fontWeight: active ? 700 : 400, fontSize: 14.5,
              cursor: active ? "default" : "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
              fontFamily: "inherit",
            }}>
            <Icon name={o.icon} size={16} color={active ? UI.state.fg : UI.text.secondary}/>
            {o.id === "auto" ? "אוטומטי" : o.label}
          </button>
        );
      })}
    </div>
  );
}

export { getThemePref };
